import React, { useState, useRef, useEffect } from "react";
import OrderCard from "./OrderCard";
import ProductListCard from "./ProductListCard";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { ReactionType, SenderType, type BlockContent, type CommandVariable } from '../../../../types/chat-message';
import { RevenueReport } from '../CustomResponse/RevenueReport';
import { ProfitLossReport } from '../CustomResponse/ProfitLossReport';
import { IncomeExpenseReport } from '../CustomResponse/IncomeExpenseReport';
import { StockReport } from '../CustomResponse/StockReport';
import { LikeIcon, DislikeIcon, CopyIcon, CopyIconSuccess, CopyIconSuccessCircle, QuoteIcon, ThinkingIcon } from "@assets/icon";
import type { ChatMessage } from '../../../../types';
import { formatChatTime } from "@utils/index";
import { CopyToClipboard } from "react-copy-to-clipboard";
import CitationButton from "./CitationButton";
import FollowUpSuggestions from "./FollowUpSuggestions";
import ConfirmSwitchMessage from "./ConfirmSwitchMessage";
import "./ChatAdmin.scss";
import { ChatAgentType } from "@constants/chat-agents";
import { stripMarkdown } from "@utils/textUtils";
import { getButtonStyle } from "@utils/buttonUtils";
import { useTranslation } from "react-i18next";
import DislikeBlock from "./DislikeBlock";
import { ToastMessage } from "@hooks/useChat";
import { alignBlockContentsWithFullContent } from "@utils/chat-message";

// Simplified runner using ReactMarkdown to handle all markdown features (###, **, etc.)
const renderFormattedText = (
  text: string,
  isInline?: boolean,
  isFinal?: boolean,
  hasValidBlocks?: boolean
): React.ReactNode => {
  if (!text) return null;
  console.log('Original text to render:', text, 'isFinal:', isFinal, 'hasValidBlocks:', hasValidBlocks);
  let processed = text;
  processed = processed.replace(/\|.*$/s, '');

  // 1. XỬ LÝ LỖI STREAM KHI CỘNG CHUỖI DỞ DANG
  if (!isFinal) {
    const trailingAsterisks = processed.match(/\*+$/);
    if (trailingAsterisks) {
      processed = processed.replace(/\*+$/, '');
    }

    const boldMatches = processed.match(/\*\*/g) || [];
    if (boldMatches.length % 2 !== 0) {
      processed += "**";
    }
  }

  // 2. SỬA REGEX LIST-ITEM
  if (hasValidBlocks) {
    processed = processed.replace(/(?<![\n\*])([ \t]*\*(?!\*)[ \t]+)/g, '\n\n$1');

    // 3. Dọn dẹp lỗi hiển thị thừa cụm dấu * trống
    processed = processed.replace(/^\s*\*\*\*\s*$/gm, '');

    // 4. Cắt bỏ các câu hỏi ẩn hệ thống sau dấu '|'
    if (processed.includes('|')) {
      processed = processed.split('|')[0];
    }

    processed = processed.replace(/(?<!\n)\n(?!\n)/g, '\n\n');
  }

  const Wrapper = isInline ? "span" : "div";

  return (
    <Wrapper className={`markdown-content ${isInline ? 'inline' : ''}`}>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => isInline ? <>{children}</> : <p>{children}</p>,
          code({ inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || "");
            return !inline && match ? (
              <SyntaxHighlighter
                {...props}
                style={materialDark}
                language={match[1]}
                PreTag="div"
              >
                {String(children).replace(/\n$/, "")}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
        }}
      >
        {processed}
      </ReactMarkdown>
    </Wrapper>
  );
};


const checkIsHidden = (commandVariables?: CommandVariable[]): boolean => {
  if (!commandVariables) return false;
  const hiddenVar = commandVariables.find(v => v.name === 'hidden_message');
  if (!hiddenVar) return false;
  return hiddenVar.value === 'true';
};

const getBlockByType = (blockContents: BlockContent[] | undefined, type: string): BlockContent | undefined =>
  blockContents?.find(b => b.type === type);

const getButtonsFromBlockContents = (blockContents: BlockContent[] | undefined): any[] => {
  const textBlock = blockContents?.find(b => b.type === 'TEXT' && (b.payload as any).buttons);
  return (textBlock?.payload as any)?.buttons || [];
};

interface ChatAdminProps {
  onQuestionClick?: (question: string) => void;
  messageId?: string;
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
  onThinkingStop?: () => void;
  chatMessage?: ChatMessage;
  message: string;
  handleAgentChange?: (agent: ChatAgentType) => void;
  isLastMessage?: boolean;
  handleForwardToAgent?: (targetAgent: ChatAgentType, userQuestion: string | undefined, botMessage: ChatMessage | undefined, botMessageId?: string) => void;
  handleRateMessage?: (messageId: string, action: 'LIKE' | 'DISLIKE', dislikeReason?: string) => Promise<void>;
  onQuote?: (message: ChatMessage) => void;
  activeAgent?: ChatAgentType;
  onConfirmStay?: (messageId: string, blockContents?: import('../../../../types/chat-message').BlockContent[]) => void;
  onConfirmLeave?: (messageId: string, pendingAgent: ChatAgentType) => void;
  setToastMessage?: (toast: NonNullable<ToastMessage>) => void;
}


// ... imports

const ChatAdmin: React.FC<ChatAdminProps> = ({
  message,
  onQuestionClick,
  onButtonClick,
  onThinkingStop,
  chatMessage,
  handleAgentChange,
  isLastMessage,
  handleForwardToAgent,
  handleRateMessage,
  onQuote,
  activeAgent,
  onConfirmStay,
  onConfirmLeave,
  setToastMessage,
}) => {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState<ReactionType | null>(
    chatMessage?.actionMessage as ReactionType | null ?? null
  );
  const [showToastMessage, setShowToastMessage] = useState<boolean>(false);
  const [toastType, setToastType] = useState<string>("");
  const [showDislikeBlock, setShowDislikeBlock] = useState<boolean>(false);

  const isFromHistory = chatMessage?._isHistory === true;
  const isBypassAnimation = isFromHistory ||
    chatMessage?.skipAnimation === true ||
    activeAgent === ChatAgentType.CSKH ||
    chatMessage?.typeChat === ChatAgentType.CSKH ||
    chatMessage?.responseMode === 'single' ||
    chatMessage?.fullContent === "Nghiệp vụ chưa hỗ trợ trên trình duyệt, vui lòng thử lại trên app điện thoại" ||
    message === "Nghiệp vụ chưa hỗ trợ trên trình duyệt, vui lòng thử lại trên app điện thoại";

  // ─── Typewriter animation ──────────────────────────────────────────────────
  const [displayedMessage, setDisplayedMessage] = useState(isBypassAnimation ? message : "");
  const [isAnimating, setIsAnimating] = useState(false);
  const typedTextRef = useRef(isBypassAnimation ? message : "");
  const targetTextRef = useRef(message);
  const isAnimatingRef = useRef(false);
  const animTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const [isStopped, setIsStopped] = useState(chatMessage?.isStopped === true);
  const isStoppedRef = useRef(chatMessage?.isStopped === true);

  // Dispatch typewriter-animation-change event to inform parent components (like InputMessage)
  useEffect(() => {
    if (isLastMessage && !isBypassAnimation) {
      window.dispatchEvent(new CustomEvent('typewriter-animation-change', {
        detail: { isAnimating, messageId: chatMessage?.id || chatMessage?.messageId }
      }));
    }
    return () => {
      if (isLastMessage && !isBypassAnimation) {
        window.dispatchEvent(new CustomEvent('typewriter-animation-change', {
          detail: { isAnimating: false, messageId: chatMessage?.id || chatMessage?.messageId }
        }));
      }
    };
  }, [isAnimating, isLastMessage, chatMessage?.id, chatMessage?.messageId, isBypassAnimation]);

  // Listen to skip-typewriter-animation (Stop) event
  useEffect(() => {
    if (isBypassAnimation || !isLastMessage) return;

    const handleStop = () => {
      console.log('[Typewriter] Stop/Freeze animation event received');
      isStoppedRef.current = true;
      setIsStopped(true);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      isAnimatingRef.current = false;
      setIsAnimating(false);
    };

    window.addEventListener('skip-typewriter-animation', handleStop);
    return () => window.removeEventListener('skip-typewriter-animation', handleStop);
  }, [isBypassAnimation, isLastMessage]);

  useEffect(() => {
    if (chatMessage?.isStopped) {
      isStoppedRef.current = true;
      setIsStopped(true);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      isAnimatingRef.current = false;
      setIsAnimating(false);
      if (isBypassAnimation) {
        setDisplayedMessage(message);
        typedTextRef.current = message;
      }
    } else {
      isStoppedRef.current = false;
      setIsStopped(false);
    }
  }, [chatMessage?.isStopped, message, isBypassAnimation]);

  useEffect(() => {
    // Nếu đã dừng stream, không cập nhật gì thêm cho text hiển thị
    if (isStoppedRef.current) {
      return;
    }

    targetTextRef.current = message;

    // Reset/Sync ngay lập tức nếu là tin nhắn từ lịch sử hoặc cần bypass animation
    if (isBypassAnimation) {
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      typedTextRef.current = message;
      setDisplayedMessage(message);
      isAnimatingRef.current = false;
      setIsAnimating(false);
      return;
    }

    // Reset animation khi đổi sang tin nhắn hoàn toàn khác
    if (message.length > 0 && typedTextRef.current.length > 0 && !message.startsWith(typedTextRef.current)) {
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      typedTextRef.current = message;
      setDisplayedMessage(message);
      isAnimatingRef.current = false;
      setIsAnimating(false);
      isStoppedRef.current = false;
      setIsStopped(false);
      return;
    }

    // Nếu chưa chạy animation và độ dài text hiển thị ngắn hơn target, bắt đầu loop
    if (!isAnimatingRef.current && typedTextRef.current.length < message.length) {
      isAnimatingRef.current = true;
      setIsAnimating(true);

      const CHAR_DELAY_MS = 21

      const tick = () => {
        const currentTarget = targetTextRef.current;
        const currentTyped = typedTextRef.current;

        if (currentTyped.length < currentTarget.length) {
          const gap = currentTarget.length - currentTyped.length;
          // Tăng tốc gõ nếu khoảng cách trễ quá xa
          const step = Math.min(gap > 100 ? 10 : gap > 50 ? 6 : 3, gap);
          const nextText = currentTarget.slice(0, currentTyped.length + step);
          typedTextRef.current = nextText;
          setDisplayedMessage(nextText);
          animTimerRef.current = setTimeout(tick, CHAR_DELAY_MS);
        } else {
          isAnimatingRef.current = false;
          setIsAnimating(false);
        }
      };

      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      tick();
    }
  }, [message, isBypassAnimation]);

  // Sync displayedMessage khi messageId thay đổi (chuyển sang message khác hoàn toàn)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Nếu tin nhắn đang stream (chưa final) và không phải từ lịch sử, không force-sync để tránh làm gián đoạn animation khi đổi messageId tạm -> thật
    if (chatMessage?.isFinal !== true && !chatMessage?._isHistory) {
      return;
    }

    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    typedTextRef.current = message;
    setDisplayedMessage(message);
    isAnimatingRef.current = false;
    setIsAnimating(false);
  }, [chatMessage?.messageId, chatMessage?.id]);
  // ──────────────────────────────────────────────────────────────────────────

  const formattedTime = formatChatTime(chatMessage?.time);
  const messageContentRef = useRef<HTMLDivElement>(null);

  // Reset feedback when message or agent changes - restore from actionMessage
  useEffect(() => {
    setFeedback(chatMessage?.actionMessage as ReactionType | null ?? null);
  }, [chatMessage?.messageId, chatMessage?.agentType]);

  // Auto hide toast after 2 seconds
  useEffect(() => {
    if (showToastMessage) {
      const timer = setTimeout(() => {
        setShowToastMessage(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showToastMessage]);

  const handleQuestionClick = (question: string) => {
    if (onQuestionClick) {
      onQuestionClick(question);
    }
  };

  const getToastMessage = (): string => {
    return toastType === "COPY" ? "common.copySuccess" : "common.likeAndDislikeMessage";
  };

  const handleCopySuccess = () => {
    setShowToastMessage(true);
    setToastType("COPY");
  };

  // change to handleReaction, and pass prop like, dislike
  const handleReaction = (reaction: ReactionType) => {
    if (feedback === reaction) return;
    setFeedback(reaction);
    setToastType(reaction);

    if (reaction === ReactionType.DISLIKE) {
      setShowDislikeBlock(true);
    } else {
      setShowToastMessage(true);
    }

    if (chatMessage?.id && handleRateMessage) {
      handleRateMessage(chatMessage.id, reaction);
    }
  }

  const handleDislikeBlockClose = () => {
    setShowDislikeBlock(false);
  };

  const handleDislikeSubmitReason = (reason: string) => {
    if (chatMessage?.id && handleRateMessage) {
      handleRateMessage(chatMessage.id, ReactionType.DISLIKE, reason);
    }
  };

  const handleShowDislikeToast = () => {
    setShowToastMessage(true);
  };

  const handleRedirectToOtherAgent = () => {
    if (!chatMessage?.targetAgent) {
      console.warn('No targetAgent specified in message', chatMessage);
      return;
    }

    const targetAgent = chatMessage.targetAgent as ChatAgentType;

    // Validate that targetAgent is a valid ChatAgentType
    if (!Object.values(ChatAgentType).includes(targetAgent)) {
      console.warn('Invalid targetAgent:', targetAgent, 'Valid agents:', Object.values(ChatAgentType));
      return;
    }

    // Forward message to target agent
    if (handleForwardToAgent) {
      console.log("has handleForwardToAgent forward to agent");
      handleForwardToAgent(
        targetAgent,
        chatMessage.msgUserFwToNewAgent,
        chatMessage,
        chatMessage.msgBotFwToNewAgentId
      );
    } else {
      console.log("no handleForwardToAgent forward to agent");
      // Fallback: just change agent
      handleAgentChange?.(targetAgent);
    }
  }

  const isOrderMessage = chatMessage?.blockContents?.some(b => b.type === 'ORDER_INFO');
  const isProductListMessage = chatMessage?.blockContents?.some(b => b.type === 'PRODUCT_LIST');
  const isCompleteMessage = chatMessage?.isMsgHasBg;

  const REPORT_BLOCK_TYPES = ['REVENUE_REPORT', 'PROFIT_LOSS_REPORT', 'INCOME_EXPENSE_REPORT', 'STOCK_REPORT'] as const;
  type ReportBlockType = typeof REPORT_BLOCK_TYPES[number];

  const activeReportType = chatMessage?.blockContents
    ?.find(b => REPORT_BLOCK_TYPES.includes(b.type as ReportBlockType))?.type as ReportBlockType | undefined;

  const isReportMessage = !!activeReportType;

  const isSpecialCard = isOrderMessage || isProductListMessage || isReportMessage || isCompleteMessage;
  const isTypingComplete =
    isFromHistory ||
    !!isSpecialCard ||
    (isStopped && isBypassAnimation) ||
    (chatMessage?.isFinal === true && !isAnimating && displayedMessage.length >= message.length);

  const hasTextContent = message && message.trim().length > 0;
  const hasBlockTextContent = chatMessage?.blockContents?.some(
    (block: any) => block.type === 'TEXT' && block.payload?.text?.trim()
  );
  const hasButtons = chatMessage?.buttons && chatMessage.buttons.length > 0;
  const hasImage = chatMessage?.blockContents?.some((block: any) => block.payload?.imageUrl);
  const hasMessageContent = !!(hasTextContent || hasBlockTextContent || hasButtons || isSpecialCard || hasImage);

  const getCopyText = () => {

    const appendNotify = (text: string): string => {
      if (!chatMessage?.suggestAgentNotify) return text;
      return [text, `${stripMarkdown(chatMessage.suggestAgentNotify)} tại đây`].filter(Boolean).join('\n');
    };

    if (isOrderMessage && chatMessage?.blockContents) {
      const titleBlock = chatMessage.blockContents.find(b => b.type === 'TEXT' && (b.payload as any).text?.toLowerCase().includes('thông tin đơn hàng'));
      const statusBlock = chatMessage.blockContents.find(b => b.type === 'TEXT' && b !== titleBlock && !(b.payload as any).buttons);
      const buttonBlock = chatMessage.blockContents.find(b => b.type === 'TEXT' && (b.payload as any).buttons && (b.payload as any).buttons!.length > 0);
      const orderInfoBlock = chatMessage.blockContents.find(b => b.type === 'ORDER_INFO');

      const buttons = (buttonBlock?.payload as any)?.buttons || [];
      const paymentButtons = buttons.filter((btn: any) =>
        btn.commandVariables?.some((v: any) => v.name === 'payment_method')
      );

      const statusText = (statusBlock?.payload as any)?.text || "Tạo đơn hàng thành công!";
      const titleText = (titleBlock?.payload as any)?.text || "THÔNG TIN ĐƠN HÀNG";
      const orderStatus = (titleBlock?.payload as any)?.status;

      const products = (orderInfoBlock?.payload as any)?.products || [];
      let productLine = "";
      if (products.length > 0) {
        const productNames = products.map((p: any) => `${p.quantity === '1' ? '' : p.quantity + ' '}${p.name}`).join(', ');
        const totalAmount = products[0].totalAmount || '0';
        productLine = `${productNames} ${totalAmount}`;
      } else {
        productLine = "Tổng tiền 0";
      }

      const paymentMethod = (buttonBlock?.payload as any)?.paymentMethod;
      const paymentNamesText = paymentButtons.map((b: any) => b.name).join(' ');
      const paymentName = paymentMethod || paymentNamesText;
      const paymentLine = paymentName ? `Thanh toán ${paymentName}` : "Thanh toán";

      return appendNotify([
        statusText.replace("!", "") + "!",
        titleText,
        orderStatus,
        productLine,
        paymentLine,
      ].filter(Boolean).map(text => stripMarkdown(text)).join('\n'));
    }

    if (isProductListMessage && chatMessage?.blockContents) {
      const textBlock = chatMessage.blockContents.find(b => b.type === 'TEXT');
      const textPayload = textBlock?.payload as any;
      const titleText = textPayload?.text || "";

      const listBlock = chatMessage.blockContents.find(b => b.type === 'PRODUCT_LIST');
      const listPayload = listBlock?.payload as any;
      const products = listPayload?.products || [];

      const productsText = products.map((product: any) => {
        const isQuantityZero = product.quantity === 0 || product.quantity === '0';
        const hasValidQuantity = product.quantity !== null && product.quantity !== undefined && product.quantity !== '' && !isQuantityZero && product.quantity !== 'Hết hàng';
        const isOutOfStock = product.quantity === 'Hết hàng' || isQuantityZero || product.quantity === null || (product.addToCart === false && !hasValidQuantity);

        let displayQuantity = product.quantity ?? '';

        if (product.quantity === 'Hết hàng') {
          displayQuantity = '0';
        } else if (isOutOfStock) {
          displayQuantity = 'Hết hàng';
        }

        const nameLine = product.name || "";
        const codePart = product.productCode ? `${product.productCode} ` : "";
        const quantityPart = displayQuantity !== '' ? `Tồn kho: ${displayQuantity}` : "";
        const codeAndQtyLine = (codePart + quantityPart).trim();
        const categoryLine = `Phân loại: ${product.category || "-"} ${product.price || ""}`.trim();

        return [nameLine, codeAndQtyLine, categoryLine].filter(Boolean).join('\n');
      }).join('\n\n');

      return appendNotify([stripMarkdown(titleText), productsText].filter(Boolean).join('\n\n'));
    }

    if (isReportMessage && chatMessage?.blockContents) {
      const reportBlock = getBlockByType(chatMessage.blockContents, activeReportType!);
      const data = (reportBlock?.payload as any)?.data;

      const fmtCurrency = (v: number) =>
        new Intl.NumberFormat('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v) + 'đ';
      const fmtRate = (r?: number) =>
        r !== undefined ? ` ${r > 0 ? '▲' : r < 0 ? '▼' : ''} ${Math.abs(r).toFixed(2)}%` : '';

      if (activeReportType === 'INCOME_EXPENSE_REPORT' && data) {
        const { timeRange, totalCashIn, totalCashOut, remindCash } = data;
        return appendNotify([
          `BÁO CÁO THU CHI ${timeRange || ''}`.trim(),
          `Tổng thu: ${fmtCurrency(totalCashIn)}`,
          `Tổng chi: ${fmtCurrency(totalCashOut)}`,
          `Chênh lệch: ${remindCash >= 0 ? '+' : ''}${fmtCurrency(remindCash)}`,
        ].join('\n'));
      }

      if (activeReportType === 'PROFIT_LOSS_REPORT' && data) {
        const { timeRange, totalRevenue, totalCost, totalProfit, totalProfitOther,
          totalRevenueRate, totalCostRate, totalProfitRate, totalProfitOtherRate } = data;
        return appendNotify([
          `BÁO CÁO LÃI LỖ ${timeRange || ''}`.trim(),
          `Lợi nhuận: ${fmtRate(totalProfitRate)}  ${fmtCurrency(totalProfit)}`,
          `Doanh thu: ${fmtRate(totalRevenueRate)}  ${fmtCurrency(totalRevenue)}`,
          `Giá vốn: ${fmtRate(totalCostRate)}   ${fmtCurrency(totalCost)}`,
          `Lợi nhuận khác: ${fmtRate(totalProfitOtherRate)}  ${fmtCurrency(totalProfitOther)}`,
        ].join('\n'));
      }

      if (activeReportType === 'REVENUE_REPORT' && data) {
        const { startDate, endDate, revenue, orders, customers, topProducts = [] } = data;
        const dateLabel = startDate === endDate ? startDate : `${startDate} - ${endDate}`;

        const titleBlock = chatMessage.blockContents.find((b: any) => b.type === 'TEXT');
        const title = (titleBlock?.payload as any)?.text || `DOANH THU BÁN HÀNG ${dateLabel || ''}`.trim();

        const fmtMetric = (label: string, metric?: any) => {
          if (!metric) return null;
          const dir = metric.direction === 'up' ? '▲' : metric.direction === 'down' ? '▼' : '';
          const rateStr = dir ? `${dir}${(metric.rate ?? 0).toFixed(2)}%` : '';
          const valueStr = (metric.formattedValue ?? '').replace(' VND', ' đ');
          return rateStr ? `${label} ${rateStr}   ${valueStr}` : `${label}   ${valueStr}`;
        };

        const lines: string[] = [
          title,
          fmtMetric('Doanh thu', revenue),
          fmtMetric('Đơn hàng', orders),
          fmtMetric('Khách hàng', customers),
        ].filter(Boolean) as string[];

        if (topProducts.length > 0) {
          lines.push('Hàng hóa bán chạy');
          topProducts.forEach((p: any) => {
            lines.push(`${p.name}   ${p.quantity} sản phẩm`);
          });
        }

        return appendNotify(lines.join('\n'));
      }

      if (activeReportType === 'STOCK_REPORT' && data) {
        const { date, totalQuantity = 0, totalAmount = 0, totalOutOfStockQuantity = 0, totalCanSellQuantity = 0 } = data;

        const titleBlock = chatMessage.blockContents.find((b: any) => b.type === 'TEXT');
        const title = (titleBlock?.payload as any)?.text || (date ? `BÁO CÁO KHO HÔM NAY ${date}` : 'BÁO CÁO KHO HÀNG');

        const fmtNumber = (n: number) => new Intl.NumberFormat('vi-VN').format(n);
        const floor = Math.floor(totalQuantity);
        const decimal = totalQuantity % 1;
        const quantityStr = decimal !== 0
          ? `${fmtNumber(floor)},${Math.round(decimal * 100)} sản phẩm`
          : `${fmtNumber(floor)} sản phẩm`;

        return appendNotify([
          title,
          `Số lượng: ${quantityStr}`,
          `Giá trị tồn: ${fmtCurrency(totalAmount)}`,
          `Đã hết hàng: ${totalOutOfStockQuantity} sản phẩm`,
          `Sản phẩm còn bán: ${totalCanSellQuantity} sản phẩm`,
        ].join('\n'));
      }
    }

    return appendNotify(stripMarkdown(isStopped ? displayedMessage : message));
  };

  const renderCompleteBlock = () => {
    const text = chatMessage?.text || '';
    return (
      <div className="bg-white p-3 rounded-xl">
        <div className="block-contents">
          <div className="block-content-item">
            <div className="block-text">
              {renderFormattedText(text, false, chatMessage?.isFinal !== false)}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-3 mt-3">
          {chatMessage?.buttons?.map((button: any, index: number) => {
            const isDisabled = button.enabledColor === null || isFromHistory || !isLastMessage;

            const baseClassName = 'px-4 py-1.5 rounded-[8px] border text-sm font-medium shadow-sm';
            const hoverClassName = isDisabled ? '' : 'hover:opacity-80';

            return (
              <button
                key={button.id || index}
                className={`${baseClassName} ${hoverClassName}`}
                style={{
                  transition: isDisabled ? 'none' : 'all 0.3s ease',
                  cursor: isDisabled ? 'default' : 'pointer',
                  borderColor: isDisabled ? '#E7EAF3B2' : 'var(--colour-neutral-surface)',
                  color: isDisabled ? '#b1b1b1ff' : 'var(--colour-primary-shade-md)',
                  backgroundColor: isDisabled ? '#E7EAF3B2' : 'var(--colour-primary-light-xxl)',
                  ...getButtonStyle(button, isDisabled),
                }}
              onClick={() => {
                  if (isDisabled) return;
                  onButtonClick?.(button.name, button.id, checkIsHidden(button.commandVariables));

                  // Xử lý chuyển agent khi user nhấn "Không" trên flow CHANGE_MODE_PENDING
                  if (
                    chatMessage?.typeChangeFlow === 'CHANGE_MODE_PENDING' &&
                    chatMessage?.targetAgent &&
                    button.intent === 'change_flow_confirm'
                  ) {
                    const confirmVar = button.commandVariables?.find((v: any) => v.name === 'confirm');
                    if (confirmVar?.value === 'false') {
                      // User xác nhận rời đi → chuyển sang agent đích
                      console.log('[ChatAdmin] CHANGE_MODE_PENDING "Không" clicked, switching to:', chatMessage.targetAgent);
                      handleAgentChange?.(chatMessage.targetAgent as ChatAgentType);
                    }
                  }
                }}
                type="button"
                title={isFromHistory ? 'Tin nhắn từ lịch sử' : ''}
              >
                {button.name}
              </button>
            );
          })}
        </div>
      </div>
    )
  }

  const renderReport = (): React.ReactNode => {

    if (!activeReportType || !chatMessage?.blockContents) return null;
    const reportBlock = getBlockByType(chatMessage.blockContents, activeReportType);
    const payload = reportBlock?.payload as any;
    // if (!payload?.data) return null;

    const buttons = getButtonsFromBlockContents(chatMessage.blockContents) || chatMessage?.buttons;
    const commonProps = { onButtonClick, isLastMessage, isFromHistory: chatMessage?._isHistory };

    const sortedBlocks = [...chatMessage.blockContents].sort((a, b) => ((a.payload as any)?.order ?? 0) - ((b.payload as any)?.order ?? 0));
    const firstTextBlock = sortedBlocks.find(b => b.type === 'TEXT' && (b.payload as any)?.text && !(b.payload as any)?.buttons);
    const firstTextBlockText = (firstTextBlock?.payload as any)?.text as string | undefined;

    switch (activeReportType) {
      case 'REVENUE_REPORT':
        return <RevenueReport data={payload.data} buttons={buttons} {...commonProps} text={firstTextBlockText || payload.text} />;
      case 'PROFIT_LOSS_REPORT':
        return <ProfitLossReport data={payload.data} buttons={buttons} {...commonProps} text={firstTextBlockText || payload.text} />;
      case 'INCOME_EXPENSE_REPORT':
        return <IncomeExpenseReport data={payload.data} buttons={buttons} {...commonProps} text={firstTextBlockText || payload.text} />;
      case 'STOCK_REPORT':
        return <StockReport data={payload.data} buttons={buttons} {...commonProps} text={firstTextBlockText || payload.text} />;
      default:
        return null;
    }
  };

  // Render blockContents if available
  const renderContent = () => {
    if (isOrderMessage && chatMessage?.blockContents) {
      return <OrderCard blockContents={chatMessage.blockContents} onButtonClick={onButtonClick} onThinkingStop={onThinkingStop} isLastMessage={isLastMessage} chatMessageTime={chatMessage.createdAt || chatMessage.time} isFromHistory={chatMessage?._isHistory} setToastMessage={setToastMessage} />;
    }

    if (isProductListMessage && chatMessage?.blockContents) {
      return <ProductListCard blockContents={chatMessage.blockContents} onButtonClick={onButtonClick} onThinkingStop={onThinkingStop} isLastMessage={isLastMessage} isFromHistory={chatMessage?._isHistory} messageId={chatMessage?.messageId || chatMessage?.id} messageIdStr={chatMessage?.messageIdStr} />;
    }

    if (isReportMessage) {
      const rendered = renderReport();
      if (rendered) return rendered;
    }

    if (isCompleteMessage && chatMessage?.blockContents) {
      return renderCompleteBlock();
    }


    // Dùng displayedMessage để render text đang animate
    const displayStyleText = displayedMessage;
    const isFinal = chatMessage?.isFinal === true;

    const hasValidBlocks =
      chatMessage?.blockContents &&
      chatMessage.blockContents.length > 0 &&
      chatMessage.blockContents.some((b: any) =>
        (b.type === 'TEXT' && b.payload?.text) || b.payload?.imageUrl || b.type !== 'TEXT'
      );

    console.error('Rendering with fallback to plain text due to incomplete blocks or ongoing stream. isFinal:', isFinal, 'hasValidBlocks:', hasValidBlocks, 'blockContents:', chatMessage?.blockContents);

    if (!isFinal || !hasValidBlocks || !isTypingComplete) {
      // console.warn('Rendering with fallback to plain text due to incomplete blocks or ongoing stream. isFinal:', isFinal, 'hasValidBlocks:', hasValidBlocks, 'blockContents:', chatMessage?.blockContents);
      return (
        <span style={{ display: 'block' }}>
          {renderFormattedText(displayStyleText, false, true, true)}
        </span>
      );
    }

    // Sort blocks by order
    const sortedBlocks = [...chatMessage.blockContents].sort((a, b) => {
      const orderA = a.payload?.order ?? 0;
      const orderB = b.payload?.order ?? 0;
      return orderA - orderB;
    });

    // Render blockContents
    return (
      <div className="block-contents">
        {sortedBlocks.map((block: BlockContent, index: number) => {
          const payload = block.payload as any;



          if (block.type !== 'TEXT' || !payload?.text) {
            if (payload?.imageUrl) {
              return (
                <div key={index} className="block-content-item" style={{ marginBottom: index < sortedBlocks.length - 1 ? '8px' : '0' }}>
                  <img
                    src={payload.imageUrl}
                    alt=""
                    style={{ maxWidth: '100%', borderRadius: '8px' }}
                  />
                </div>
              );
            }
            return null;
          }

          const { text, isBold, level = 0 } = payload;
          const paddingLeft = level * 16;

          return (
            <div key={index} className="block-content-item" style={level > 1 ? { paddingLeft: `${paddingLeft}px` } : {}}>
              <div
                className="block-text"
                style={{
                  fontWeight: isBold ? 600 : 400,
                  marginBottom: index < sortedBlocks.length - 1 ? '8px' : '0',
                }}
              >
                {renderFormattedText(text, false, true, true)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  return (
    <React.Fragment>
      {/* Confirm-switch message type */}
      {chatMessage?.confirmSwitchAgent && (
        <div className="chat-admin flex flex-col mt-[10px]">
          <div className="flex mb-2 text-purple-400">
            <ThinkingIcon className={`w-5 h-5 ${(isLastMessage && !chatMessage?._isHistory) ? 'animate-pulse' : ''}`} />
          </div>
          <div className="flex items-center w-full">
            <div className="flex flex-col w-full">
              <div className="border-chat-bot break-words rounded-lg p-3">
                <ConfirmSwitchMessage
                  messageId={chatMessage.id || chatMessage.messageId || ''}
                  pendingAgent={chatMessage.confirmSwitchAgent}
                  isDisabled={!!chatMessage.isConfirmDisabled}
                  originalBlockContents={chatMessage.originalBlockContents}
                  onStay={(id, bc) => onConfirmStay?.(id, bc)}
                  onLeave={(id, agent) => onConfirmLeave?.(id, agent)}
                />
              </div>
              <div className="chat-admin-actions flex items-center justify-start gap-2 mt-3 font-size-time">
                <button
                  className={`chat-action-btn chat-action-btn-like ${feedback === ReactionType.LIKE ? 'active' : ''}`}
                  onClick={() => handleReaction(ReactionType.LIKE)}
                  aria-label="Thích"
                  type="button"
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content="Thích"
                  disabled={feedback === ReactionType.DISLIKE}
                  style={{
                    display: feedback === ReactionType.DISLIKE ? 'none' : 'flex',
                    cursor: feedback === ReactionType.LIKE ? 'default' : 'pointer'
                  }}
                >
                  <LikeIcon />
                </button>

                <button
                  className={`chat-action-btn chat-action-btn-dislike ${feedback === ReactionType.DISLIKE ? 'active' : ''}`}
                  onClick={() => handleReaction(ReactionType.DISLIKE)}
                  aria-label="Không thích"
                  type="button"
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content="Không thích"
                  disabled={feedback === ReactionType.LIKE}
                  style={{
                    display: feedback === ReactionType.LIKE ? 'none' : 'flex',
                    cursor: feedback === ReactionType.DISLIKE ? 'default' : 'pointer'
                  }}
                >
                  <DislikeIcon />
                </button>

                <CopyToClipboard
                  text={getCopyText()}
                  onCopy={handleCopySuccess}
                >
                  <button
                    className="chat-action-btn chat-action-btn-copy"
                    aria-label="Sao chép"
                    type="button"
                    data-tooltip-id="chatbot-tooltip"
                    data-tooltip-content="Sao chép"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    {showToastMessage && toastType === "COPY" ? (
                      <CopyIconSuccess />
                    ) : (
                      <CopyIcon />
                    )}
                  </button>
                </CopyToClipboard>

                {activeAgent === ChatAgentType.CSKH && (
                  <button
                    className="chat-action-btn"
                    aria-label="Trích dẫn"
                    type="button"
                    data-tooltip-id="chatbot-tooltip"
                    data-tooltip-content="Trích dẫn"
                    onClick={() => {
                      if (chatMessage && onQuote) {
                        onQuote(chatMessage);
                      }
                    }}
                  >
                    <QuoteIcon />
                  </button>
                )}

                {formattedTime && (
                  <div className="chat-time">{formattedTime}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!chatMessage?.confirmSwitchAgent && (
        <>
          {showToastMessage && (
            <div className="copy-toast">
              <div className="copy-toast-content">
                <CopyIconSuccessCircle />
                {t(getToastMessage())}
              </div>
            </div>
          )}

          <div className="chat-admin flex flex-col mt-[10px]">
            <div className="flex mb-2 text-purple-400">
              <ThinkingIcon className={`w-5 h-5 ${(isLastMessage && !chatMessage?._isHistory) ? 'animate-pulse' : ''}`} />
            </div>
            <div className="flex items-center w-full">
              <div className="flex flex-col w-full">
                <div className={`border-chat-bot break-words rounded-lg ${isProductListMessage ? 'transparent-card' : 'p-3'} ${isOrderMessage ? 'bg-transparent border-none p-0' : ''} ${isReportMessage ? 'full-width' : ''}`} ref={messageContentRef}>

                  {renderContent()}

                  {(isStopped || chatMessage?.isStopped) && (
                    <div className="stop-message-warning">
                      Bạn đã dừng trả lời câu hỏi này
                    </div>
                  )}

                  {(isTypingComplete || isStopped || chatMessage?.isStopped) && chatMessage?.references && chatMessage?.references.length > 0 && (
                    <CitationButton references={chatMessage?.references} />
                  )}

                  {isTypingComplete && !isStopped && !chatMessage?.isStopped && !isOrderMessage && !isProductListMessage && !isReportMessage && !isCompleteMessage && chatMessage?.buttons && chatMessage?.buttons.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3 mb-2">
                      {chatMessage?.buttons.map((button: any, index: number) => {
                        const isDisabled = button.enabledColor === null || isFromHistory || !isLastMessage;

                        const baseClassName = 'px-4 py-1.5 rounded-[8px] border text-sm font-medium shadow-sm';
                        const hoverClassName = isDisabled ? '' : 'hover:opacity-80';

                        return (
                          <button
                            key={button.id || index}
                            className={`${baseClassName} ${hoverClassName}`}
                            style={{
                              transition: isDisabled ? 'none' : 'all 0.3s ease',
                              cursor: isDisabled ? 'default' : 'pointer',
                              borderColor: isDisabled ? '#E7EAF3B2' : 'var(--colour-neutral-surface)',
                              color: isDisabled ? '#b1b1b1ff' : 'var(--colour-primary-shade-md)',
                              backgroundColor: isDisabled ? '#E7EAF3B2' : 'var(--colour-primary-light-xxl)',
                              ...getButtonStyle(button, isDisabled),
                            }}
                            onClick={() => !isDisabled && onButtonClick?.(button.name, button.id, checkIsHidden(button.commandVariables))}
                            type="button"
                            title={isFromHistory ? 'Tin nhắn từ lịch sử' : ''}
                          >
                            {button.name}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {isTypingComplete && !isStopped && !chatMessage?.isStopped && chatMessage?.suggestAgentNotify && (() => {
                    // Check if main content is empty (no text content to show border separator for)
                    const hasTextContent = message && message.trim().length > 0;
                    const hasBlockTextContent = chatMessage?.blockContents?.some(
                      (block: any) => block.type === 'TEXT' && block.payload?.text?.trim()
                    );
                    const hasButtons = chatMessage?.buttons && chatMessage.buttons.length > 0;
                    const hasMainContent = hasTextContent || hasBlockTextContent || hasButtons;

                    return (
                      <div className="mt-3">
                        <span className="text-[#242428]">
                          {renderFormattedText(chatMessage?.suggestAgentNotify || "", true)}
                          <button
                            onClick={handleRedirectToOtherAgent}
                            className={`ml-1 font-medium ${chatMessage?.targetAgent && isLastMessage && !chatMessage?._isHistory ? 'text-[var(--colour-semantic-cyan-base)] underline hover:opacity-80' : 'text-[#242428]'}`}
                            disabled={!chatMessage?.targetAgent || !isLastMessage || chatMessage?._isHistory}
                            style={{
                              cursor: chatMessage?.targetAgent && isLastMessage && !chatMessage?._isHistory ? 'pointer' : 'default',
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              display: 'inline'
                            }}
                          >
                            tại đây
                          </button>
                        </span>
                      </div>
                    );
                  })()}

                  {isTypingComplete && !isStopped && !chatMessage?.isStopped && chatMessage?.followUpQuestions && chatMessage?.followUpQuestions.length > 0 && !chatMessage?._isHistory && (
                    <FollowUpSuggestions
                      questions={chatMessage?.followUpQuestions}
                      onQuestionClick={handleQuestionClick}
                      disabled={!isLastMessage}
                    />
                  )}

                  {(isTypingComplete || isStopped || chatMessage?.isStopped) && hasMessageContent && (
                    <div className={`chat-admin-actions flex items-center justify-start gap-2 mt-3 font-size-time`}>
                      <button
                        className={`chat-action-btn chat-action-btn-like ${feedback === ReactionType.LIKE ? 'active' : ''}`}
                        onClick={() => handleReaction(ReactionType.LIKE)}
                        aria-label="Thích"
                        type="button"
                        data-tooltip-id="chatbot-tooltip"
                        data-tooltip-content="Thích"
                        disabled={feedback === ReactionType.DISLIKE}
                        style={{
                          display: feedback === ReactionType.DISLIKE ? 'none' : 'flex',
                          cursor: feedback === ReactionType.LIKE ? 'default' : 'pointer'
                        }}
                      >
                        <LikeIcon />
                      </button>

                      <button
                        className={`chat-action-btn chat-action-btn-dislike ${feedback === ReactionType.DISLIKE ? 'active' : ''}`}
                        onClick={() => handleReaction(ReactionType.DISLIKE)}
                        aria-label="Không thích"
                        type="button"
                        data-tooltip-id="chatbot-tooltip"
                        data-tooltip-content="Không thích"
                        disabled={feedback === ReactionType.LIKE}
                        style={{
                          display: feedback === ReactionType.LIKE ? 'none' : 'flex',
                          cursor: feedback === ReactionType.DISLIKE ? 'default' : 'pointer'
                        }}
                      >
                        <DislikeIcon />
                      </button>

                      <CopyToClipboard
                        text={getCopyText()}
                        onCopy={handleCopySuccess}
                      >
                        <button
                          className="chat-action-btn chat-action-btn-copy"
                          aria-label="Sao chép"
                          type="button"
                          data-tooltip-id="chatbot-tooltip"
                          data-tooltip-content="Sao chép"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          {showToastMessage && toastType === "COPY" ? (
                            <CopyIconSuccess />
                          ) : (
                            <CopyIcon />
                          )}
                        </button>
                      </CopyToClipboard>



                      {/* Reply Button - Only in CSKH */}
                      {activeAgent === ChatAgentType.CSKH && (
                        <button
                          className="chat-action-btn"
                          aria-label="Trích dẫn"
                          type="button"
                          data-tooltip-id="chatbot-tooltip"
                          data-tooltip-content="Trích dẫn"
                          onClick={() => {
                            if (chatMessage && onQuote) {
                              onQuote(chatMessage);
                            }
                          }}
                        >
                          <QuoteIcon />
                        </button>
                      )}

                      {formattedTime && (
                        <div className="chat-time">{formattedTime}</div>
                      )}
                    </div>
                  )}
                </div>
              </div >
            </div >
          </div >
        </>
      )}

      {showDislikeBlock && chatMessage?.id && (
        <DislikeBlock
          onCloseDislikeBlock={handleDislikeBlockClose}
          onSubmitReason={handleDislikeSubmitReason}
          onShowToast={handleShowDislikeToast}
        />
      )}
    </React.Fragment>
  );
};

export default React.memo(ChatAdmin, (prevProps, nextProps) => {
  return (
    prevProps.messageId === nextProps.messageId &&
    prevProps.message === nextProps.message &&
    prevProps.isLastMessage === nextProps.isLastMessage &&
    prevProps.activeAgent === nextProps.activeAgent &&
    prevProps.chatMessage === nextProps.chatMessage
  );
});
